import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, Calendar, Gauge, Palette, Tag } from "lucide-react";
import { apiClient, fileUrl, formatPrice, PHONE_TEL, PHONE_NUMBER } from "../lib/api";
import Seo from "../components/Seo";

const FALLBACK = "https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?q=80&w=1600&auto=format&fit=crop";

const MotorcycleDetail = () => {
  const { id } = useParams();
  const [moto, setMoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    apiClient
      .get(`/motorcycles/${id}`)
      .then((r) => setMoto(r.data))
      .catch(() => setMoto(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="bg-zinc-950 text-white pt-32 pb-24 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="aspect-video bg-zinc-900 animate-pulse mb-6" />
          <div className="h-12 w-2/3 bg-zinc-900 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!moto) {
    return (
      <div className="bg-zinc-950 text-white pt-32 pb-24 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h1 className="font-display text-4xl uppercase mb-6">Motor bulunamadı</h1>
          <Link to="/" className="text-orange-500 uppercase tracking-widest text-sm">
            Ana Sayfaya Dön
          </Link>
        </div>
      </div>
    );
  }

  const images = moto.images && moto.images.length > 0 ? moto.images.map(fileUrl) : [FALLBACK];
  const whatsappMsg = encodeURIComponent(
    `Merhaba, ${moto.year} ${moto.brand} ${moto.model} hakkında bilgi almak istiyorum.`
  );

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: `${moto.year} ${moto.brand} ${moto.model}`,
    image: images,
    description:
      moto.description ||
      `${moto.year} ${moto.brand} ${moto.model} ${moto.condition === "sifir" ? "sıfır" : "ikinci el"} motosiklet Gensan Motors İstanbul'da satışta.`,
    brand: { "@type": "Brand", name: moto.brand },
    sku: moto.id,
    category: "Motosiklet",
    productionDate: String(moto.year),
    itemCondition: moto.condition === "sifir" ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
    additionalProperty: [
      { "@type": "PropertyValue", name: "Yıl", value: String(moto.year) },
      ...(moto.mileage != null ? [{ "@type": "PropertyValue", name: "Kilometre", value: `${moto.mileage} km` }] : []),
      ...(moto.engine_cc != null ? [{ "@type": "PropertyValue", name: "Motor Hacmi", value: `${moto.engine_cc} cc` }] : []),
      ...(moto.color ? [{ "@type": "PropertyValue", name: "Renk", value: moto.color }] : []),
    ],
    offers: {
      "@type": "Offer",
      priceCurrency: "TRY",
      price: String(moto.price),
      availability: "https://schema.org/InStock",
      itemCondition: moto.condition === "sifir" ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
      url: `https://gensanmotors.com.tr/motor/${moto.id}`,
      seller: { "@type": "Organization", name: "Gensan Motors" },
    },
  };

  return (
    <div data-testid="motorcycle-detail-page" className="bg-zinc-950 text-white pt-28 pb-24 min-h-screen">
      <Seo
        title={`${moto.year} ${moto.brand} ${moto.model}`}
        description={`${moto.year} ${moto.brand} ${moto.model} ${moto.condition === "sifir" ? "sıfır" : "ikinci el"} motosiklet ${formatPrice(moto.price)} fiyatla Gensan Motors İstanbul'da satışta. ${moto.description ? moto.description.slice(0, 120) : "Detaylar ve iletişim için tıklayın."}`}
        keywords={`${moto.brand} ${moto.model}, ${moto.brand} fiyat, ${moto.condition === "sifir" ? "sıfır" : "ikinci el"} ${moto.brand} motosiklet`}
        path={`/motor/${moto.id}`}
        image={images[0]}
        jsonLd={productSchema}
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Link
          to={moto.condition === "sifir" ? "/sifir-motorlar" : "/ikinci-el-motorlar"}
          data-testid="back-link"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-orange-500 text-sm uppercase tracking-widest mb-8 transition-colors"
        >
          <ArrowLeft size={16} /> Geri Dön
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Images */}
          <div className="lg:col-span-3">
            <div className="aspect-[4/3] overflow-hidden bg-zinc-900 mb-4">
              <img
                src={images[activeIdx]}
                alt={`${moto.brand} ${moto.model}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = FALLBACK; }}
              />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    data-testid={`thumb-${i}`}
                    className={`aspect-square overflow-hidden border-2 ${
                      activeIdx === i ? "border-orange-500" : "border-transparent hover:border-zinc-700"
                    } transition-colors`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
                moto.condition === "sifir" ? "bg-orange-600 text-white" : "bg-white text-black"
              }`}>
                {moto.condition === "sifir" ? "Sıfır" : "2. El"}
              </span>
              {moto.is_featured && (
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest bg-black border border-orange-600 text-orange-500">
                  Öne Çıkan
                </span>
              )}
            </div>

            <p className="text-orange-500 font-bold uppercase tracking-widest text-sm mb-2">
              {moto.brand}
            </p>
            <h1 data-testid="motorcycle-title" className="font-display text-4xl md:text-5xl font-bold uppercase leading-none tracking-tight mb-6">
              {moto.model}
            </h1>
            <div className="text-5xl font-display font-bold text-white mb-8">
              {formatPrice(moto.price)}
            </div>

            <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 mb-8">
              <div className="bg-zinc-950 p-5">
                <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                  <Calendar size={14} /> Yıl
                </div>
                <div className="text-xl font-bold">{moto.year}</div>
              </div>
              {moto.engine_cc && (
                <div className="bg-zinc-950 p-5">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                    <Gauge size={14} /> Motor
                  </div>
                  <div className="text-xl font-bold">{moto.engine_cc}cc</div>
                </div>
              )}
              {moto.mileage != null && (
                <div className="bg-zinc-950 p-5">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                    <Tag size={14} /> KM
                  </div>
                  <div className="text-xl font-bold">{moto.mileage.toLocaleString("tr-TR")}</div>
                </div>
              )}
              {moto.color && (
                <div className="bg-zinc-950 p-5">
                  <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest mb-2">
                    <Palette size={14} /> Renk
                  </div>
                  <div className="text-xl font-bold">{moto.color}</div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 mb-8">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="call-button"
                className="inline-flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-6 py-4 font-bold uppercase tracking-widest text-sm transition-colors"
              >
                <Phone size={18} /> Hemen Ara: {PHONE_NUMBER}
              </a>
              <a
                href={`https://wa.me/${PHONE_TEL.replace(/[^0-9]/g, '')}?text=${whatsappMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-button"
                className="inline-flex items-center justify-center gap-3 border border-white/40 hover:border-white hover:bg-white hover:text-black text-white px-6 py-4 font-bold uppercase tracking-widest text-sm transition-all"
              >
                <MessageCircle size={18} /> WhatsApp ile İletişim
              </a>
            </div>

            {moto.description && (
              <div className="border-t border-zinc-900 pt-6">
                <h3 className="font-impact text-sm uppercase tracking-widest text-orange-500 mb-3">Açıklama</h3>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{moto.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotorcycleDetail;
